/**
 * FeedbackDetailPanel — renders inside the expanded row of a feedback event.
 *
 * Fetches the full feedback detail via `eventsApi.getFeedback(id)` lazily on
 * mount (i.e. only when the row is actually expanded). Shows a loading spinner
 * while fetching and an inline Alert on error.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { eventsApi } from '../../services/eventsApi';
import type { FeedbackEventDetail } from '../../services/types';
import CreateIssueButton from './CreateIssueButton';

interface Props {
  feedbackId: string;
}

const FeedbackDetailPanel: React.FC<Props> = ({ feedbackId }) => {
  const [detail, setDetail] = useState<FeedbackEventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    eventsApi
      .getFeedback(feedbackId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Detail konnte nicht geladen werden.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [feedbackId]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1} p={2}>
        <CircularProgress size={20} />
        <Typography variant="body2">Lade Detail …</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }} role="alert">
        {error}
      </Alert>
    );
  }

  if (!detail) return null;

  return (
    <Box p={2}>
      <Stack spacing={1.5} divider={<Divider flexItem />}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            Kategorie / Status
          </Typography>
          <Typography variant="body2">
            {detail.category} · {detail.status}
          </Typography>
        </Box>

        <Box>
          <Typography variant="overline" color="text.secondary">
            Beschreibung
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {detail.description}
          </Typography>
        </Box>

        <Box>
          <Typography variant="overline" color="text.secondary">
            Nutzer / Seite
          </Typography>
          <Typography variant="body2">
            {detail.userEmail} ({detail.userId}) — {detail.pageUrl}
          </Typography>
        </Box>

        <Box>
          <Typography variant="overline" color="text.secondary" display="block" gutterBottom>
            GitHub-Issue
          </Typography>
          <CreateIssueButton
            eventKind="feedback"
            eventId={detail.id}
            existingIssueUrl={detail.githubIssueUrl}
            existingIssueNumber={detail.githubIssueNumber}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default FeedbackDetailPanel;
